import { Paper, Title, Stack, Group, Text, Badge, Divider, Button, Tooltip } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import { messages } from '../messages';
import { toDateFormat } from 'utils/timeDateUtils';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import { Loading } from '@carbon/react';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';
import { useGetActivePlansQuery, useGetPaymentMethodsQuery, useRefreshMySubscriptionMutation } from 'api/subscriptionPlanEndpoints';
import { useCallback, useMemo, useState } from 'react';
import UnsubscribeFromPaidPlanModal from './UnsubscribeFromPaidPlanModal';
import { Checkmark, Purchase, TrashCan } from '@carbon/icons-react';
import { useGetMyInvoicesQuery, useRetryInvoicePaymentMutation } from 'api/billingEndpoints';
import { RetryInvoicePaymentResultDto } from 'api/dtos/billingDtos';
import { useStripe } from '@stripe/react-stripe-js';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import { showSnackbar } from 'store/reducers/snackbarSlice';
import styles from './ActiveSubscription.module.scss';

const isPaymentActionRequired = (status?: string, paymentStatus?: string, requiresAction?: boolean) => (
    Boolean(requiresAction) ||
    status?.toLowerCase() === 'past_due' ||
    status?.toLowerCase() === 'unpaid' ||
    status?.toLowerCase() === 'incomplete' ||
    paymentStatus?.toLowerCase() === 'payment_required'
);

const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error !== 'object' || error === null || !('data' in error)) {
        return fallback;
    }

    const { data } = error as { data?: unknown };

    if (typeof data === 'string') {
        return data;
    }

    if (typeof data === 'object' && data !== null && 'message' in data) {
        const { message } = data as { message?: unknown };

        if (typeof message === 'string') {
            return message;
        }
    }

    return fallback;
};

const toRetryPaymentResult = (data: unknown): RetryInvoicePaymentResultDto => {
    if (typeof data !== 'object' || data === null) {
        return {};
    }

    const record = data as Record<string, unknown>;
    const { paymentIntentClientSecret } = record;

    return {
        paid: typeof record.paid === 'boolean' ? record.paid : undefined,
        requiresAction: typeof record.requiresAction === 'boolean' ? record.requiresAction : undefined,
        paymentIntentClientSecret: typeof paymentIntentClientSecret === 'string'
            ? paymentIntentClientSecret
            : null,
        requiresPaymentMethod: typeof record.requiresPaymentMethod === 'boolean'
            ? record.requiresPaymentMethod
            : undefined,
    };
};

export const ActiveSubscription = () => {
    const { activeSubscription, isFetching, isError, refetch, isPaidPlan } = useGetCurrentSubscription(); 
    const stripe = useStripe();
    const dispatch = useDispatch<AppDispatch>();

    const intl = useIntl();

    const { data: subscriptionPlans, isFetching: activePlansFetching, refetch: refetchActivePlans } = useGetActivePlansQuery();
    const {
        data: paymentMethods,
        isFetching: paymentMethodsFetching,
        isError: paymentMethodsError,
        refetch: refetchPaymentMethods,
    } = useGetPaymentMethodsQuery(undefined, { skip: !isPaidPlan });
    const paymentRequired = isPaymentActionRequired(
        activeSubscription?.status,
        typeof activeSubscription?.paymentStatus === 'string' ? activeSubscription.paymentStatus : undefined,
        activeSubscription?.requiresPaymentAction === true,
    );
    const {
        data: invoices,
        isFetching: invoicesFetching,
        refetch: refetchInvoices,
    } = useGetMyInvoicesQuery(undefined, { skip: !paymentRequired || !activeSubscription?.id });
    const [retryInvoicePayment, { isLoading: isRetryingPayment }] = useRetryInvoicePaymentMutation();
    const [refreshSubscription, { isLoading: isRefreshingSubscription }] = useRefreshMySubscriptionMutation();

    const [requestedCancellation, setRequestedCancellation] = useState<boolean>(false);

    const selectedPaymentMethod = useMemo(
        () => paymentMethods?.find(method => method.isDefault) ?? paymentMethods?.[0] ?? null,
        [paymentMethods],
    );
    const hasPaymentMethods = (paymentMethods?.length ?? 0) > 0;
    const payableInvoice = useMemo(() => invoices?.find(invoice => (
        Boolean(activeSubscription?.id) &&
        invoice.userSubscriptionId === activeSubscription?.id &&
        invoice.canBePaid === true &&
        invoice.status.toLowerCase() === 'open' &&
        invoice.paidAt == null &&
        invoice.amountDue > 0
    )), [activeSubscription?.id, invoices]);

    const onRequestCancellation = () => {
        setRequestedCancellation(true);
    };

    const onCancelRequestedCancellation = () => {
        setRequestedCancellation(false);
    };

    const notify = useCallback((message: string, severity: 'success' | 'error') => {
        dispatch(showSnackbar({ message, severity }));
    }, [dispatch]);

    const refreshSubscriptionState = useCallback(async () => {
        await refreshSubscription().unwrap().catch(() => undefined);
        await Promise.allSettled([
            refetch(),
            refetchInvoices(),
        ]);
    }, [refetch, refetchInvoices, refreshSubscription]);

    const onRetryPayment = useCallback(async () => {
        if (!payableInvoice || !hasPaymentMethods || !stripe) {
            return;
        }

        const body = selectedPaymentMethod && !selectedPaymentMethod.isDefault
            ? {
                paymentMethodId: selectedPaymentMethod.paymentToken,
                setAsDefault: true,
            }
            : {};

        try {
            const response = await retryInvoicePayment({
                invoiceId: payableInvoice.id,
                body,
            }).unwrap();
            const { data } = response as { data?: unknown };
            const {
                paid,
                requiresAction,
                paymentIntentClientSecret,
                requiresPaymentMethod,
            }: RetryInvoicePaymentResultDto = toRetryPaymentResult(data);

            if (paid === true) {
                await refreshSubscriptionState();
                notify(intl.formatMessage(activeSubscriptionMessages.retryPaymentSuccess), 'success');
                return;
            }

            if (requiresAction === true) {
                const clientSecret = typeof paymentIntentClientSecret === 'string'
                    ? paymentIntentClientSecret
                    : '';

                if (!clientSecret) {
                    notify(intl.formatMessage(activeSubscriptionMessages.retryPaymentMissingClientSecret), 'error');
                    return;
                }

                const confirmationResult = await stripe.confirmCardPayment(clientSecret);

                if (confirmationResult.error) {
                    const stripeErrorMessage = typeof confirmationResult.error.message === 'string'
                        ? confirmationResult.error.message
                        : intl.formatMessage(activeSubscriptionMessages.retryPaymentError);

                    notify(
                        stripeErrorMessage,
                        'error',
                    );
                    return;
                }

                await refreshSubscriptionState();
                notify(intl.formatMessage(activeSubscriptionMessages.retryPaymentSuccess), 'success');
                return;
            }

            if (requiresPaymentMethod === true) {
                notify(intl.formatMessage(activeSubscriptionMessages.retryPaymentRequiresPaymentMethod), 'error');
                return;
            }

            await refreshSubscriptionState();
        } catch (error) {
            notify(
                getApiErrorMessage(error, intl.formatMessage(activeSubscriptionMessages.retryPaymentError)),
                'error',
            );
        }
    }, [
        hasPaymentMethods,
        intl,
        notify,
        payableInvoice,
        refreshSubscriptionState,
        retryInvoicePayment,
        selectedPaymentMethod,
        stripe,
    ]);

    if (isFetching || activePlansFetching || paymentMethodsFetching || invoicesFetching) {
        return <Loading />;
    }

    if (!subscriptionPlans) {
        return <ErrorActionState onClick={refetchActivePlans} />;
    }

    if (paymentMethodsError) {
        return <ErrorActionState onClick={refetchPaymentMethods} />;
    }

    if (isError) {
        return <ErrorActionState onClick={refetch} />;
    }

    if (!activeSubscription) {
        return (
            <Paper shadow="sm" p="xl" radius="md" withBorder>
                <Title order={2} size="h3" mb="lg">
                    {intl.formatMessage(messages.activeSubscription)}
                </Title>
                <EmptyState title={intl.formatMessage(activeSubscriptionMessages.noSubscription)} />
            </Paper>
        );
    }

    const activePlan = subscriptionPlans.find(plan => plan.id === activeSubscription.planId);
    const paymentMethod = selectedPaymentMethod;
    const hasNoPaymentMethods = isPaidPlan && (paymentMethods?.length ?? 0) === 0;
    const isRetryPaymentDisabled = !hasPaymentMethods || !stripe || isRetryingPayment || isRefreshingSubscription;
    const subscriptionStatus = (() => {
        if (paymentRequired) {
            return intl.formatMessage(activeSubscriptionMessages.paymentRequiredStatus);
        }

        return activeSubscription.cancelAtPeriodEnd ? intl.formatMessage(messages.expires) : intl.formatMessage(messages.active);
    })();
    const planName = activeSubscription.planName || intl.formatMessage(activeSubscriptionMessages.unknownPlan);
    const formattedAmount = typeof activeSubscription.amount === 'number' && activeSubscription.currency
        ? activeSubscription.amount.toLocaleString('pl-PL', {
            style: 'currency',
            currency: activeSubscription.currency.toUpperCase(),
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
        : null;
    const statusBadgeColor = (() => {
        if (paymentRequired || activeSubscription.hasAccess === false) {
            return 'red';
        }

        if (activeSubscription.cancelAtPeriodEnd) {
            return 'orange';
        }

        return 'green';
    })();

    return (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Title order={2} size="h3" mb="lg">
                {intl.formatMessage(messages.activeSubscription)}
            </Title>

            <Stack gap="lg">
                <Group align="flex-start" gap="md" wrap="nowrap">
                    <div className={styles.planIcon}>
                        <Purchase size={24} />
                    </div>

                    <Stack gap={4} className={styles.planSummary}>
                        <Text size="lg" fw={700}>
                            {intl.formatMessage(messages.planName, {
                                name: planName,
                            })}
                        </Text>
                        {formattedAmount && (
                            <Text size="xl" fw={800} className={styles.planPrice}>
                                {formattedAmount}
                                {' / '}
                                {intl.formatMessage(messages.monthLower)}
                            </Text>
                        )}
                        <Badge
                            className={styles.statusBadge}
                            color={statusBadgeColor}
                            variant="light"
                        >
                            {subscriptionStatus}
                        </Badge>
                    </Stack>
                </Group>

                {paymentRequired && (
                    <div className={styles.paymentRequiredWarning}>
                        <Text size="sm" fw={700}>
                            {intl.formatMessage(activeSubscriptionMessages.paymentRequiredTitle)}
                        </Text>
                        <Text size="sm">
                            {intl.formatMessage(activeSubscriptionMessages.paymentRequiredDescription)}
                        </Text>
                        {payableInvoice && (
                            <Tooltip
                                label={intl.formatMessage(activeSubscriptionMessages.retryPaymentNoPaymentMethodTooltip)}
                                disabled={hasPaymentMethods}
                                withArrow
                            >
                                <span className={styles.retryPaymentButtonWrapper}>
                                    <Button
                                        className={styles.retryPaymentButton}
                                        variant="outline"
                                        disabled={isRetryPaymentDisabled}
                                        loading={isRetryingPayment || isRefreshingSubscription}
                                        onClick={onRetryPayment}
                                    >
                                        {intl.formatMessage(activeSubscriptionMessages.retryPayment)}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                )}

                {isPaidPlan && (
                    <Stack gap="xs">
                        {activeSubscription.currentPeriodEnd && (
                            <Group gap="xs">
                                <Text size="sm" c="dimmed">
                                    {intl.formatMessage(messages.nextPayment)}
                                    :
                                </Text>
                                <Text size="sm" fw={600}>
                                    {toDateFormat(activeSubscription.currentPeriodEnd)}
                                </Text>
                            </Group>
                        )}
                        {hasNoPaymentMethods && (
                            <Text size="sm" fw={600} className={styles.paymentMethodWarning}>
                                {intl.formatMessage(activeSubscriptionMessages.noPaymentMethodWarning)}
                            </Text>
                        )}
                        {paymentMethod && (
                            <Group gap="xs">
                                <Text size="sm" c="dimmed">
                                    {intl.formatMessage(messages.paymentMethod)}
                                    :
                                </Text>
                                <Text size="sm" fw={600}>
                                    {paymentMethod.cardBrand.toUpperCase()}
                                    {' **** '}
                                    {paymentMethod.lastFourDigits}
                                </Text>
                            </Group>
                        )}
                    </Stack>
                )}

                <Divider />

                {!!activePlan?.features.length && (
                    <Stack gap="sm">
                        <Text size="sm" fw={700}>
                            {intl.formatMessage(messages.planIncludes)}
                        </Text>
                        <div className={styles.featuresGrid}>
                            {activePlan.features.map(feature => (
                                <Group key={feature} gap="xs" wrap="nowrap" className={styles.featureItem}>
                                    <Checkmark size={16} className={styles.featureIcon} />
                                    <Text size="sm">{feature}</Text>
                                </Group>
                            ))}
                        </div>
                    </Stack>
                )}

                {isPaidPlan && !activeSubscription.cancelAtPeriodEnd && !paymentRequired && (
                    <Group justify="flex-end">
                        <Button
                            variant="outline"
                            color="red"
                            leftSection={<TrashCan size={16} />}
                            onClick={onRequestCancellation}
                        >
                            {intl.formatMessage(messages.cancelRenewal)}
                        </Button>
                    </Group>
                )}
            </Stack>

            <UnsubscribeFromPaidPlanModal confirmationModalOpen={requestedCancellation} onModalClose={onCancelRequestedCancellation} />
        </Paper>
    );
};

const activeSubscriptionMessages = defineMessages({
    noPaymentMethodWarning: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.noPaymentMethodWarning',
        defaultMessage: 'Dodaj metodę płatności, żeby w kolejnym okresie rozliczeniowym opłacić subskrypcję i zachować możliwość korzystania z aplikacji.',
    },
    paymentRequiredStatus: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.paymentRequiredStatus',
        defaultMessage: 'Płatność wymagana',
    },
    paymentRequiredTitle: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.paymentRequiredTitle',
        defaultMessage: 'Nieuregulowana płatność',
    },
    paymentRequiredDescription: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.paymentRequiredDescription',
        defaultMessage: 'Nie udało się opłacić subskrypcji. Ureguluj płatność lub dodaj poprawną metodę płatności, żeby odzyskać dostęp i zachować możliwość korzystania z aplikacji.',
    },
    retryPayment: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPayment',
        defaultMessage: 'Ponów płatność',
    },
    retryPaymentNoPaymentMethodTooltip: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPaymentNoPaymentMethodTooltip',
        defaultMessage: 'Najpierw dodaj metodę płatności',
    },
    retryPaymentSuccess: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPaymentSuccess',
        defaultMessage: 'Płatność została ponowiona.',
    },
    retryPaymentRequiresPaymentMethod: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPaymentRequiresPaymentMethod',
        defaultMessage: 'Płatność się nie udała. Dodaj albo wybierz inną metodę płatności.',
    },
    retryPaymentMissingClientSecret: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPaymentMissingClientSecret',
        defaultMessage: 'Płatność wymaga dodatkowego potwierdzenia, ale nie otrzymano danych do potwierdzenia.',
    },
    retryPaymentError: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.retryPaymentError',
        defaultMessage: 'Nie udało się ponowić płatności.',
    },
    unknownPlan: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.unknownPlan',
        defaultMessage: 'subskrypcja',
    },
    noSubscription: {
        id: 'src.Screens.Dashboard.Subscription.ActiveSubscription.noSubscription',
        defaultMessage: 'Brak aktywnej subskrypcji',
    },
});
